/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from '@src/app/shared/message.service';
import { VATRate } from '@src/app/model/VATRate';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { TenantService } from '@src/app/shared/tenant.service';
import { AbstractObject } from '@src/app/model/abstract-object';
import { OmcSettings } from '@src/app/model/OmcSettings';
@Injectable({
  providedIn: 'root'
})
export class VatRateService {

  private http = inject(HttpClient);
  private msgSvc = inject(MessageService);
  private tenantSvc = inject(TenantService);
  private vatRates$: Observable<VATRate[]> | undefined;
  private vatCodes$: Observable<AbstractObject[]> | undefined;
  private vatRates: VATRate[] = [];

  public getVatRates(): Observable<VATRate[]> {
    if (!this.vatRates$) {
      const url = this.tenantSvc.getPath('vatRates');
      this.vatRates$ = this.http.get<VATRate[]>(url).pipe(
        tap((rates) => {
          this.vatRates = rates;
        }),
        catchError(error => {
          this.msgSvc.showError(error, 'Getting VAT Rates');
          return [];
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.vatRates$;
  }

  public getVatCodes(): Observable<AbstractObject[]> {

    if (!this.vatCodes$) {
      this.vatCodes$ = this.getVatRates().pipe(
        map(rates => {
          const codes: AbstractObject[] = [];
          rates.forEach( r => {
            if (!r.endDate) {
              const code = r.vatCode === 'x' ? $localize `Exempt` : r.vatCode
              codes.push({id: r.vatCode, name: `${code} (${r.vatRate}%)`});
            }
          })
          return codes;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.vatCodes$;
  }

  private matchRate(vr: VATRate, code: string, txnDate: string) {
    if (vr.vatCode === code 
    && vr.effectiveDate <= txnDate 
    && (!vr.endDate || vr.endDate >= txnDate)) {
      return true
    } else {
      return false;
    }
  }

  public getRate(code: string, txnDate: string) {
    const r = this.vatRates.find(vr => this.matchRate(vr, code, txnDate));
    if (r) {
      return r.vatRate;
    }
    this.msgSvc.showErrorMessage($localize `Unable to find applicable VAT Rate for code ${code} on ${txnDate}`)
    return 0;
  }

  public vatRegistered(o: OmcSettings) {
      if (o?.vatNumber && o.vatNumber != 'unregistered' ) {
          return true;
      }
      return false
  }  

}
